import { addHours, addMinutes, addDays } from 'date-fns'
import { Select, Grommet, ThemeContext } from 'grommet'
import { grommet } from 'grommet/themes'
import { useEffect, useState, useRef, useMemo } from 'react'
import { DayPicker } from 'react-day-picker'
import { createPortal } from 'react-dom'

import { ImageWithPlaceholder } from '@components/ImageWithPlaceholder'
import { workingTheme } from '@constants/themeGrommet'
import {
  timeChoose,
  timeChooseFifteen,
  timeChooseOneHour,
  timeStamp,
} from '@constants/timeChoose'
import { timeZone } from '@constants/timeZone'
import { createSelectOptionsFromArray } from '@helpers/createSelectOptionsFromArray'
import { filterOutPastTimes } from '@helpers/filterOutPastTimes'

import { dayPickerConfig } from './config'
import {
  Popup,
  CalendarWrapper,
  CalendarText,
  InfoDateWrapper,
  TimeZone,
  ButtonSubmit,
  WrapStep,
  CalendarTextSubtitle,
  CalendarTextWrapper,
  ContWrapDiv,
  ButtonWrapper,
  PopupWrapper,
  CloseBtn,
  TimeZoneWrapper,
  ButtonSubmitWrapper,
  DurabilityBlock,
  DurabilityText,
  DurabilityButton,
  TimeWrapper,
  TimeButtonWrapper,
  TimeText,
  WrapperPopUp,
} from './styles'

export function PopupFirst({ onClick, modalBookCall, handleClose }) {
  const currentDate = () => {
    if (sessionStorage.getItem('selected')) {
      return new Date(sessionStorage.getItem('selected'))
    }
    const currDate = new Date()

    if (currDate.getDay() === 0) return addDays(currDate, 1)
    if (currDate.getDay() === 6) return addDays(currDate, 2)

    return currDate
  }

  const [timeDuration, setTimeDuration] = useState(
    () => +(sessionStorage.getItem('timeRange') ?? 1)
  )

  const getTimeInterval = useMemo(() => {
    switch (timeDuration) {
      case 1:
        return timeChooseFifteen
      case 2:
        return timeChoose
      case 3:
        return timeChooseOneHour
      default:
        return timeChooseFifteen
    }
  }, [timeDuration])

  const timeZoneOptionsList = useMemo(
    () => createSelectOptionsFromArray(Object.keys(timeZone)),
    [timeZone]
  )

  const [selected, setSelected] = useState(currentDate())
  const [clock, setClock] = useState('')
  const [duration, setDuration] = useState('')
  const [intervals, setIntervals] = useState(getTimeInterval)
  const [timeZoneSelected, setTimeZoneSelected] = useState('')
  const [timeZoneList, setTimeZoneList] = useState(timeZoneOptionsList)
  let bookDate = selected

  const bookCallWrapperRef = useRef()

  const [hours, minutes] = clock.split(':')
  if (hours && minutes && selected) {
    bookDate = new Date(selected.setHours(0, 0, 0, 0))
    bookDate = addHours(new Date(bookDate), +hours)
    bookDate = addMinutes(new Date(bookDate), +minutes)
  }

  useEffect(() => {
    setClock(sessionStorage.getItem('time') ?? '')
    setDuration(sessionStorage.getItem('duration') ?? '')
    setTimeZoneSelected(sessionStorage.getItem('timeZone') ?? '')
  }, [])

  const dateUtc = bookDate

  const handleTimeStamp = e => {
    setDuration(e.target.value)
    sessionStorage.setItem('duration', e.target.value)
    switch (e.target.value) {
      case '15 mins':
        sessionStorage.setItem('timeRange', 1)
        return setTimeDuration(1)
      case '30 mins':
        sessionStorage.setItem('timeRange', 2)
        return setTimeDuration(2)
      case '1 hour':
        sessionStorage.setItem('timeRange', 3)
        return setTimeDuration(3)
      default:
        return setTimeDuration(1)
    }
  }

  const handleTime = e => {
    setClock(e.target.value)
    sessionStorage.setItem('time', e.target.value)
  }

  const clearTime = () => {
    setClock('')
    sessionStorage.removeItem('time')
  }

  useEffect(() => {
    const chooseDuration = getTimeInterval
    const filteredChooseDuration = filterOutPastTimes(
      chooseDuration,
      selected,
      timeZoneSelected
    )

    setIntervals(filteredChooseDuration)
  }, [selected, timeZoneSelected, timeDuration])

  const isWeekend = date => date.getDay() === 0 || date.getDay() === 6

  const handleSearch = text => {
    const timeZoneSearch = ({ label }) =>
      label.toUpperCase().includes(text.toUpperCase())
    setTimeZoneList(timeZoneList.filter(zone => timeZoneSearch(zone)))

    if (!text) {
      setTimeZoneList(timeZoneOptionsList)
    }

    if (!timeZoneList.length) {
      setTimeZoneList(timeZoneOptionsList.filter(zone => timeZoneSearch(zone)))
    }
  }

  const handleTimeZoneChange = ({ value }) => {
    sessionStorage.setItem('timeZone', value)
    setTimeZoneSelected(value)
    setTimeZoneList(timeZoneOptionsList)
    clearTime()
  }

  const handleSubmit = () => {
    sessionStorage.setItem('selected', dateUtc.toISOString())
    sessionStorage.setItem('userDuration', dateUtc)
    onClick()
  }

  const resetAction = event => {
    event.stopPropagation()
  }

  const closePopUp = event => {
    handleClose()
    event.stopPropagation()
  }

  return createPortal(
    <WrapperPopUp>
      <PopupWrapper onClick={closePopUp}>
        <Popup ref={bookCallWrapperRef} onClick={resetAction}>
          <WrapStep>
            <ContWrapDiv>step 0{modalBookCall}/03</ContWrapDiv>
            <CloseBtn onClick={handleClose}>Close</CloseBtn>
          </WrapStep>
          <InfoDateWrapper>
            <CalendarWrapper>
              <CalendarText>
                Schedule a meetup
                <br /> with Modsen team
                <ImageWithPlaceholder
                  width="45"
                  height="42"
                  alt="Black hexagon"
                  src="/images/black-orange-hexagon.webp"
                />
              </CalendarText>
              <DayPicker
                mode="single"
                selected={selected}
                onSelect={setSelected}
                fromDate={new Date()}
                disabled={isWeekend}
                required
                {...dayPickerConfig}
              />
            </CalendarWrapper>
            <CalendarWrapper>
              <CalendarText>
                How much time do you
                <br />
                plan to spend on the call?
              </CalendarText>
              <ButtonWrapper>
                {timeStamp.map(({ id, time }) => (
                  <DurabilityBlock key={`${id}_${time}`}>
                    <DurabilityButton
                      type="radio"
                      value={time}
                      name="durability"
                      id={id}
                      onClick={handleTimeStamp}
                      defaultChecked={
                        time === sessionStorage.getItem('duration')
                      }
                    />
                    <DurabilityText htmlFor={id}>{time}</DurabilityText>
                  </DurabilityBlock>
                ))}
              </ButtonWrapper>
              <CalendarTextWrapper>
                <CalendarTextSubtitle>
                  <Grommet theme={grommet}>
                    <ThemeContext.Extend value={workingTheme}>
                      <Select
                        value={timeZoneSelected}
                        className="select"
                        dropHeight="285px"
                        labelKey="label"
                        valueKey={{ key: 'value', reduce: true }}
                        placeholder="What is your time zone?"
                        options={timeZoneList}
                        onChange={handleTimeZoneChange}
                        onSearch={handleSearch}
                      />
                    </ThemeContext.Extend>
                  </Grommet>
                </CalendarTextSubtitle>
              </CalendarTextWrapper>
              <TimeZoneWrapper>
                <TimeZone>
                  {intervals.map(({ id, time }) => (
                    <TimeButtonWrapper
                      key={`${id}_${time}`}
                      className="checked-time"
                    >
                      <TimeWrapper
                        type="radio"
                        value={time}
                        name="time"
                        id={id}
                        onClick={handleTime}
                        defaultChecked={time === sessionStorage.getItem('time')}
                      />
                      <TimeText htmlFor={id}>{time}</TimeText>
                    </TimeButtonWrapper>
                  ))}
                </TimeZone>
              </TimeZoneWrapper>
              <ButtonSubmitWrapper>
                <ButtonSubmit
                  onClick={handleSubmit}
                  disabled={
                    !(selected && clock && duration && timeZoneSelected)
                  }
                >
                  Next
                </ButtonSubmit>
              </ButtonSubmitWrapper>
            </CalendarWrapper>
          </InfoDateWrapper>
        </Popup>
      </PopupWrapper>
    </WrapperPopUp>,
    document.body
  )
}
